
import { defineComponent, ref, watchEffect, toRefs } from 'vue';
import { Notify } from '@/core/services';
import { useStore } from 'vuex';
import { Actions } from '@/store/enums/StoreEnums';
import { useI18n } from 'vue-i18n';
import InnerLoader from '@/components/InnerLoader.vue';

const MAX_PDF_BYTES = 5 * 1024 * 1024;

export default defineComponent({
	props: {
		datasheetList: {
			type: Object,
			default: null
		}
	},
	components: {
		InnerLoader
	},
	setup(props, context) {
		const { datasheetList } = toRefs(props);
		const store = useStore();
		const i18n = useI18n();
		const loading = ref(false);
		const datasheetUrl = ref('');
		const datasheetMediaId = ref<number | null>(null);
		const preview = ref<Record<string, any> | null>(null);

		watchEffect(() => {
			if (datasheetList?.value) {
				datasheetUrl.value = datasheetList.value.datasheet_url || '';
				if (datasheetList.value.preview) {
					preview.value = datasheetList.value.preview;
					datasheetMediaId.value = datasheetList.value.preview.id || null;
				} else {
					preview.value = null;
					datasheetMediaId.value = null;
				}
			}
		});

		const emitChange = () => {
			context.emit('datasheet', {
				datasheet_media_id: datasheetMediaId.value,
				datasheet_url: datasheetUrl.value || null,
				preview: preview.value
			});
		};

		const onPdfChange = async (e) => {
			const file = e.target.files?.[0];
			e.target.value = '';
			if (!file) {
				return;
			}

			const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
			if (!isPdf) {
				Notify.error(i18n.t('message.ONLY_PDF_ALLOWED'));
				return;
			}

			if (file.size > MAX_PDF_BYTES) {
				Notify.error(i18n.t('message.PDF_MAX_5MB'));
				return;
			}

			if (datasheetMediaId.value) {
				await store.dispatch(Actions.REMOVE_MEDIA, datasheetMediaId.value);
			}

			loading.value = true;
			try {
				const response = await store.dispatch(Actions.CREATE_MEDIA, [file]);
				const respFiles = JSON.parse(response);
				const uploaded = respFiles[0];
				datasheetMediaId.value = uploaded.id;
				preview.value = {
					id: uploaded.id,
					src: uploaded.asset_path,
					name: file.name
				};
				emitChange();
			} catch (error) {
				Notify.error(i18n.t('message.SOMETHING_WENT_WRONG'));
			} finally {
				loading.value = false;
			}
		};

		const removePdf = () => {
			store.getters.appInstance.$messageBox
				.confirm(`${i18n.t('message.ARE_YOU_SURE_TO_DELETE_RECORD')}?`, i18n.t('message.INFO'), {
					confirmButtonText: i18n.t('message.OK'),
					cancelButtonText: i18n.t('message.CANCEL'),
					type: 'info'
				})
				.then(async () => {
					loading.value = true;
					if (datasheetMediaId.value) {
						const resp = await store.dispatch(Actions.REMOVE_MEDIA, datasheetMediaId.value);
						if (resp == 'true') {
							datasheetMediaId.value = null;
							preview.value = null;
							emitChange();
							Notify.success(i18n.t('message.RECORD_DELETED_SUCCESSFULLY'));
						}
					} else {
						preview.value = null;
						emitChange();
					}
					loading.value = false;
				})
				.catch(() => {
					loading.value = false;
				});
		};

		return {
			loading,
			datasheetUrl,
			preview,
			onPdfChange,
			removePdf,
			emitChange
		};
	}
});
